/**
 * teamsGrabaciones.js — v2
 *
 * Encuentra la grabación de Teams de una reunión y lee su transcripción.
 *
 * Son dos APIs distintas y hay que usar las dos:
 *
 *  - Microsoft Graph lista los .mp4 del OneDrive del usuario (carpeta
 *    "Grabaciones" / "Recordings").
 *
 *  - La transcripción NO está en Graph: vive en la API de streaming de
 *    SharePoint (`_api/v2.1/.../media/transcripts/`), que pide un token de otro
 *    recurso (el dominio de SharePoint, no graph.microsoft.com).
 *
 * Sobre emparejar una cita con su grabación: lo ideal sería el `iCalUid`, que
 * Teams a veces graba en el metadato del .mp4. Pero Graph no lo devuelve de
 * forma fiable en el listado de `children` —v1 ya se encontró con esto y dejó
 * el aviso en los logs—, así que no se puede depender de él.
 *
 * Lo que sí es fiable es el **nombre del archivo**: Teams lo compone como
 * `<asunto>-<yyyymmdd>_<hhmmss>-Grabación de la reunión.mp4`. De ahí salen dos
 * datos exactos: el asunto de la reunión y su hora de inicio. Eso empareja
 * mucho mejor que `createdDateTime`, que es cuando terminó de subirse.
 */

const GRAPH = 'https://graph.microsoft.com/v1.0';

export class GrabacionesError extends Error {
  constructor(mensaje, { status = null, code = null } = {}) {
    super(mensaje);
    this.name = 'GrabacionesError';
    this.status = status;
    this.code = code;
  }

  get mensajeUsuario() {
    if (this.code === 'SIN_SITIO') {
      return 'No se pudo determinar dónde vive el vídeo, así que no se puede pedir su transcripción.';
    }
    if (this.status === 401 || this.status === 403) {
      return 'No hay permiso para leer las grabaciones. Vuelve a iniciar sesión y acepta el acceso a los archivos.';
    }
    if (this.status === 404) {
      return 'Esta reunión no tiene transcripción guardada en Teams.';
    }
    return this.message;
  }
}

async function graph(ruta, token) {
  const url = /^https?:\/\//i.test(ruta) ? ruta : `${GRAPH}${ruta}`;
  const respuesta = await fetch(url, {
    headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' }
  });

  if (!respuesta.ok) {
    let detalle = `Graph respondió ${respuesta.status}`;
    try {
      const cuerpo = await respuesta.json();
      if (cuerpo.error && cuerpo.error.message) detalle = cuerpo.error.message;
    } catch {
      /* la respuesta de error no era JSON */
    }
    throw new GrabacionesError(detalle, { status: respuesta.status });
  }
  return respuesta.json();
}

/**
 * La base del sitio de SharePoint al que pertenece un archivo, sacada de su
 * `webUrl`.
 *
 * Hace falta porque la transcripción se pide contra el sitio donde vive el
 * vídeo, y ese sitio no siempre es el OneDrive de quien mira:
 *
 *   .../personal/alguien_empresa_com/Documents/Grabaciones/x.mp4
 *       → https://tenant-my.sharepoint.com/personal/alguien_empresa_com
 *   .../sites/Equipo/Documentos compartidos/General/Recordings/x.mp4
 *       → https://tenant.sharepoint.com/sites/Equipo
 *
 * Antes se componía a mano con el correo del usuario actual, lo que solo
 * funcionaba si la reunión la había grabado él mismo.
 */
export function baseDeSitio(webUrl) {
  if (!webUrl) return null;
  try {
    const url = new URL(webUrl);
    const partes = url.pathname.split('/').filter(Boolean);

    if (partes[0] === 'personal' && partes[1]) {
      return `${url.origin}/personal/${partes[1]}`;
    }
    if (partes[0] === 'sites' && partes[1]) {
      return `${url.origin}/sites/${partes[1]}`;
    }
    if (partes[0] === 'teams' && partes[1]) {
      return `${url.origin}/teams/${partes[1]}`;
    }
    return url.origin;
  } catch {
    return null;
  }
}

function iCalUidDe(item) {
  return (item.source && item.source.iCalUid) ||
    (item.media && item.media.source && item.media.source.iCalUid) ||
    (item.publication && item.publication.source && item.publication.source.iCalUid) ||
    null;
}

/**
 * La fecha y el asunto que Teams mete en el nombre del archivo.
 *
 * El formato no es uno solo: cambia con la versión de Teams y con el idioma del
 * tenant. Se admiten los que se han visto en la práctica:
 *
 *   Asunto-20260904_100000-Grabación de la reunión.mp4
 *   Asunto-20260904_1000-Meeting Recording.mp4
 *   Asunto_20260904_100000.mp4
 *   Asunto 20260904_100000.mp4
 *   Asunto-2026-09-04 10-00-00.mp4
 *
 * Si ninguno encaja no pasa nada grave: se usa la fecha del archivo. Lo que no
 * puede pasar —y pasaba— es que un nombre desconocido haga desaparecer la
 * grabación de la lista.
 */
const SELLOS = [
  // 20260904_100000 o 20260904_1000, precedido de guion, guion bajo o espacio
  /[-_ ](\d{4})(\d{2})(\d{2})[_-](\d{2})(\d{2})(\d{2})?(?=[-_. ]|$)/,
  // 2026-09-04 10-00-00  /  2026-09-04_10-00
  /[-_ ](\d{4})-(\d{2})-(\d{2})[ _T](\d{2})[-:.]?(\d{2})[-:.]?(\d{2})?(?=[-_. ]|$)/
];

export function selloDelNombre(nombre) {
  const texto = String(nombre || '');

  for (const patron of SELLOS) {
    const m = texto.match(patron);
    if (!m) continue;

    const inicio = new Date(
      Number(m[1]), Number(m[2]) - 1, Number(m[3]),
      Number(m[4]), Number(m[5]), Number(m[6] || 0)
    );
    if (isNaN(inicio.getTime())) continue;

    return {
      inicio,
      asunto: texto.slice(0, m.index).trim().replace(/[-_\s]+$/, '') || null
    };
  }

  return { inicio: null, asunto: null };
}

/** Lo que Teams añade al nombre y no es el asunto de la reunión. */
const COLETILLAS = [
  /[-_\s]*grabaci[oó]n de la reuni[oó]n\s*$/i,
  /[-_\s]*meeting recording\s*$/i,
  /[-_\s]*grabaci[oó]n\s*$/i,
  /[-_\s]*recording\s*$/i,
  /\s*\((\d+)\)\s*$/          // el "(1)" de los duplicados
];

function limpiarNombre(texto) {
  let limpio = String(texto || '').replace(/\.mp4$/i, '').trim();
  let antes;
  do {
    antes = limpio;
    for (const coletilla of COLETILLAS) limpio = limpio.replace(coletilla, '').trim();
  } while (limpio !== antes);
  return limpio.replace(/[-_\s]+$/, '').trim();
}

function aGrabacion(archivo, driveId, carpeta = null, origen = 'mi OneDrive') {
  const video = archivo.video || {};
  const nombre = limpiarNombre(archivo.name);
  const { inicio, asunto } = selloDelNombre(archivo.name);

  // Un elemento compartido apunta al original, que vive en otra unidad.
  const remoto = archivo.remoteItem || null;
  const unidad = (remoto && remoto.parentReference && remoto.parentReference.driveId) ||
    (archivo.parentReference && archivo.parentReference.driveId) ||
    driveId;
  const elemento = (remoto && remoto.id) || archivo.id;
  const web = (remoto && remoto.webUrl) || archivo.webUrl || null;

  return {
    id: archivo.id,
    // El asunto limpio es mejor título que el nombre del archivo entero.
    titulo: asunto || nombre || (carpeta && carpeta.name) || archivo.name,
    nombreArchivo: archivo.name,
    creada: archivo.createdDateTime ? new Date(archivo.createdDateTime) : null,
    // Cuándo empezó de verdad, según el nombre; si no, cuando se subió.
    inicio: inicio || (archivo.createdDateTime ? new Date(archivo.createdDateTime) : null),
    driveId: unidad,
    itemId: elemento,
    sitio: baseDeSitio(web),
    origen,
    propietario: (archivo.createdBy && archivo.createdBy.user && archivo.createdBy.user.displayName) || null,
    carpeta: carpeta ? carpeta.name : null,
    iCalUid: iCalUidDe(archivo),
    // `downloadUrl` viene firmada y caduca; sirve para reproducir sin más token.
    urlDescarga: archivo['@microsoft.graph.downloadUrl'] ||
      (remoto && remoto['@microsoft.graph.downloadUrl']) || null,
    urlWeb: web,
    duracion: video.duration != null ? video.duration / 1000 : null,
    tamano: archivo.size || 0
  };
}

/**
 * Caché del rastreo de OneDrive.
 *
 * Recorrer el OneDrive son entre cinco y treinta llamadas a Graph según cuántas
 * subcarpetas de grabaciones haya. Hacerlo al abrir el inicio y otra vez al
 * abrir cada reunión es lo que hacía que el detalle tardara tanto: el resultado
 * es el mismo y las grabaciones no aparecen de un minuto para otro.
 *
 * Vive en memoria, así que se vacía al recargar la página. Cinco minutos es de
 * sobra para moverse entre reuniones sin esperas y poco para que se quede vieja.
 */
const CACHE_TTL = 5 * 60 * 1000;
let cacheListado = { clave: null, cuando: 0, datos: null, promesa: null };

/** Fuerza un rastreo nuevo la próxima vez (tras grabar una reunión, por ejemplo). */
export function olvidarGrabaciones() {
  cacheListado = { clave: null, cuando: 0, datos: null, promesa: null };
}

/** Las transcripciones ya descargadas: volver a una reunión no debe recargarlas. */
const cacheTranscripciones = new Map();

/**
 * Grabaciones que otras personas han compartido con el usuario.
 *
 * Teams guarda la grabación en el OneDrive de **quien pulsó Grabar**, no en el
 * de cada asistente. Si la reunión la grabó otro, el archivo no está en la
 * carpeta del usuario: le llega compartido. Sin mirar aquí, la mitad de las
 * reuniones de cualquiera son invisibles.
 */
export async function grabacionesCompartidas(token) {
  const datos = await graph('/me/drive/sharedWithMe', token);
  return (datos.value || [])
    .filter(i => /\.mp4$/i.test(i.name || ''))
    .map(i => aGrabacion(i, null, null, 'compartida conmigo'));
}

/**
 * Busca grabaciones por texto en todo lo que el usuario puede ver, incluidos
 * los sitios de SharePoint de los equipos.
 *
 * Es la única forma de alcanzar las reuniones de canal: esas no van a ningún
 * OneDrive personal, sino a la biblioteca del canal.
 *
 * @param {string} token
 * @param {string} texto - normalmente el asunto de la reunión
 */
export async function buscarGrabaciones(token, texto) {
  const consulta = String(texto || '').trim();
  if (!consulta) return [];

  const respuesta = await fetch(`${GRAPH}/search/query`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      requests: [{
        entityTypes: ['driveItem'],
        query: { queryString: `${consulta} AND filetype:mp4` },
        from: 0,
        size: 25
      }]
    })
  });

  if (!respuesta.ok) {
    throw new GrabacionesError(`La búsqueda respondió ${respuesta.status}`, { status: respuesta.status });
  }

  const datos = await respuesta.json();
  const hits = (((datos.value || [])[0] || {}).hitsContainers || [])
    .flatMap(c => c.hits || []);

  return hits
    .map(h => h.resource)
    .filter(r => r && /\.mp4$/i.test(r.name || ''))
    .map(r => aGrabacion(r, (r.parentReference && r.parentReference.driveId) || null, null, 'búsqueda'));
}

/**
 * Grabaciones de Teams del usuario, con el detalle de cómo se encontraron.
 *
 * Mira la carpeta de grabaciones y un nivel de subcarpetas: Teams crea una
 * carpeta por reunión, pero según la versión deja el .mp4 suelto.
 *
 * Devuelve también un `diagnostico`, porque cuando esto no encuentra nada la
 * pregunta siempre es la misma —"¿dónde miró?"— y sin registro no hay respuesta.
 *
 * @param {string} token - token de Graph
 * @param {{dias?: number}} opciones
 * @returns {Promise<{grabaciones: Array, diagnostico: Array<string>}>}
 */
export async function listarGrabacionesConDetalle(token, { dias = 90, forzar = false } = {}) {
  const clave = `dias:${dias}`;
  const fresca = cacheListado.clave === clave &&
    Date.now() - cacheListado.cuando < CACHE_TTL;

  if (!forzar && fresca && cacheListado.datos) {
    return {
      ...cacheListado.datos,
      diagnostico: [...cacheListado.datos.diagnostico, '(reutilizado de la consulta anterior)']
    };
  }

  // Si ya hay un rastreo en marcha, esperarlo en vez de lanzar otro: el inicio
  // y el detalle piden lo mismo casi a la vez.
  if (!forzar && cacheListado.clave === clave && cacheListado.promesa) {
    return cacheListado.promesa;
  }

  const promesa = rastrearGrabaciones(token, dias);
  cacheListado = { clave, cuando: Date.now(), datos: null, promesa };

  try {
    const datos = await promesa;
    cacheListado = { clave, cuando: Date.now(), datos, promesa: null };
    return datos;
  } catch (error) {
    olvidarGrabaciones();
    throw error;
  }
}

async function rastrearGrabaciones(token, dias) {
  const diagnostico = [];

  const drive = await graph('/me/drive', token);
  const driveId = drive.id;
  diagnostico.push(`OneDrive: ${drive.name || driveId}`);

  const raiz = await graph(`/drives/${driveId}/root/children?$top=999`, token);
  const carpeta = (raiz.value || []).find(item =>
    item.folder && /recording|grabacion/i.test(item.name || '')
  );

  diagnostico.push(carpeta
    ? `Carpeta de grabaciones: "${carpeta.name}"`
    : 'Sin carpeta de grabaciones; se mira la raíz del OneDrive');

  const desde = new Date();
  desde.setDate(desde.getDate() - dias);

  const contenedorId = carpeta ? carpeta.id : 'root';
  const hijos = await graph(`/drives/${driveId}/items/${contenedorId}/children?$top=999`, token);
  const items = hijos.value || [];

  const sueltas = items
    .filter(i => i.file && /\.mp4$/i.test(i.name || ''))
    .map(a => aGrabacion(a, driveId));

  const subcarpetas = items.filter(i =>
    i.folder && (!i.createdDateTime || new Date(i.createdDateTime) >= desde)
  );

  diagnostico.push(
    `Dentro: ${sueltas.length} vídeos sueltos y ${subcarpetas.length} subcarpetas ` +
    `de los últimos ${dias} días`
  );

  const porCarpeta = await Promise.all(
    subcarpetas.map(async (sub) => {
      try {
        const dentro = await graph(`/drives/${driveId}/items/${sub.id}/children?$top=999`, token);
        return (dentro.value || [])
          .filter(i => i.file && /\.mp4$/i.test(i.name || ''))
          .map(a => aGrabacion(a, driveId, sub));
      } catch (error) {
        diagnostico.push(`No se pudo abrir la subcarpeta "${sub.name}": ${error.message}`);
        return [];
      }
    })
  );

  // Lo que otros compartieron: la mitad de las reuniones de cualquiera.
  let compartidas = [];
  try {
    compartidas = await grabacionesCompartidas(token);
    diagnostico.push(`Compartidas conmigo: ${compartidas.length} vídeos`);
  } catch (error) {
    diagnostico.push(`No se pudo leer lo compartido conmigo: ${error.message}`);
  }

  // No se descarta nada por fecha.
  //
  // Antes sí, y era un error: un vídeo cuyo nombre no encajaba con el patrón
  // esperado se quedaba con la fecha de subida del archivo, y si esa fecha caía
  // fuera del rango, el vídeo desaparecía de la lista sin dejar rastro. El
  // resultado era una pantalla vacía con seis grabaciones perfectamente
  // localizadas justo detrás. Son pocas: se devuelven todas y ya decidirá el
  // emparejamiento.
  const grabaciones = [...sueltas, ...porCarpeta.flat(), ...compartidas]
    .sort((a, b) => (b.inicio || 0) - (a.inicio || 0));

  const conSello = grabaciones.filter(g => selloDelNombre(g.nombreArchivo).inicio).length;
  diagnostico.push(
    `${grabaciones.length} grabaciones encontradas ` +
    `(${conSello} con fecha en el nombre, ${grabaciones.length - conSello} solo con la fecha del archivo)`
  );

  // Los nombres tal cual, para poder ver el patrón cuando algo no cuadra.
  grabaciones.slice(0, 8).forEach(g => {
    const cuando = g.inicio ? g.inicio.toLocaleString('es') : 'sin fecha';
    diagnostico.push(`· ${g.nombreArchivo} → "${g.titulo}", ${cuando} [${g.origen}]`);
  });
  if (grabaciones.length > 8) {
    diagnostico.push(`· … y ${grabaciones.length - 8} más`);
  }

  return { grabaciones, diagnostico };
}

/** Igual que la anterior, cuando solo hacen falta las grabaciones. */
export async function listarGrabaciones(token, opciones = {}) {
  const { grabaciones } = await listarGrabacionesConDetalle(token, opciones);
  return grabaciones;
}

function normalizarTitulo(texto) {
  return String(texto || '')
    .toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** ¿Se parecen lo suficiente como para ser la misma reunión? */
function titulosCoinciden(a, b) {
  const x = normalizarTitulo(a);
  const y = normalizarTitulo(b);
  if (!x || !y) return false;
  // En los dos sentidos: Teams recorta los asuntos largos al nombrar el
  // archivo, así que a veces el del archivo es un prefijo del de la cita.
  if (x.includes(y) || y.includes(x)) return true;

  // Si no, que compartan la mayoría de las palabras con peso.
  const palabras = (t) => new Set(t.split(' ').filter(p => p.length > 3));
  const px = palabras(x);
  const py = palabras(y);
  if (px.size === 0 || py.size === 0) return false;

  let comunes = 0;
  px.forEach(p => { if (py.has(p)) comunes++; });
  return comunes / Math.min(px.size, py.size) >= 0.6;
}

const MINUTO = 60 * 1000;

/**
 * Empareja una reunión del calendario con su grabación.
 *
 * Por orden de fiabilidad:
 *   1. `iCalUid` idéntico — exacto, cuando Graph lo devuelve.
 *   2. La hora del nombre del archivo cae dentro de la reunión (±30 min) —
 *      muy fiable, porque esa hora la escribe Teams al empezar a grabar.
 *   3. Título parecido y misma franja horaria — heurística.
 *
 * Devuelve `seguro` y `motivo` para que la interfaz pueda decir en qué se basó
 * en vez de presentar una suposición como un hecho.
 *
 * @param {Array} grabaciones
 * @param {Object} reunion - la de graphCalendar
 */
export function emparejar(grabaciones, reunion) {
  if (!reunion || !grabaciones || grabaciones.length === 0) return null;

  if (reunion.iCalUid) {
    const exacta = grabaciones.find(g => g.iCalUid && g.iCalUid === reunion.iCalUid);
    if (exacta) return { ...exacta, seguro: true, motivo: 'mismo identificador de la cita' };
  }

  const inicio = reunion.inicio ? new Date(reunion.inicio).getTime() : null;
  const fin = reunion.fin ? new Date(reunion.fin).getTime() : null;

  const candidatas = grabaciones.map(g => {
    const suInicio = g.inicio ? g.inicio.getTime() : null;
    const distancia = inicio != null && suInicio != null ? Math.abs(suInicio - inicio) : Infinity;

    // ¿Empezó a grabarse mientras la reunión transcurría?
    const dentro = suInicio != null && inicio != null &&
      suInicio >= inicio - 30 * MINUTO &&
      suInicio <= (fin != null ? fin + 30 * MINUTO : inicio + 8 * 60 * MINUTO);

    return { g, distancia, dentro, mismoTitulo: titulosCoinciden(g.titulo, reunion.titulo) };
  });

  const porHora = candidatas
    .filter(c => c.dentro)
    .sort((a, b) => a.distancia - b.distancia);

  if (porHora.length > 0) {
    const mejor = porHora.find(c => c.mismoTitulo) || porHora[0];
    return {
      ...mejor.g,
      seguro: mejor.mismoTitulo,
      motivo: mejor.mismoTitulo
        ? 'título y hora coinciden'
        : 'se grabó durante la reunión, aunque el título no coincide'
    };
  }

  const porTitulo = candidatas
    .filter(c => c.mismoTitulo && c.distancia < 6 * 60 * MINUTO)
    .sort((a, b) => a.distancia - b.distancia);

  if (porTitulo.length > 0) {
    return { ...porTitulo[0].g, seguro: false, motivo: 'título parecido y hora cercana' };
  }

  return null;
}

/** Explica por qué no se encontró grabación, para poder decirlo en pantalla. */
export function porQueNoEmpareja(grabaciones, reunion) {
  if (!grabaciones || grabaciones.length === 0) {
    return 'No hay ninguna grabación en tu OneDrive en el rango consultado.';
  }
  if (!reunion || !reunion.inicio) {
    return 'La reunión no tiene hora de inicio, así que no se puede situar en el tiempo.';
  }

  const cercanas = grabaciones
    .filter(g => g.inicio)
    .map(g => ({ g, horas: Math.abs(g.inicio - new Date(reunion.inicio)) / 3600000 }))
    .sort((a, b) => a.horas - b.horas)
    .slice(0, 3);

  if (cercanas.length === 0) {
    return `Hay ${grabaciones.length} grabaciones, pero ninguna con fecha reconocible.`;
  }

  const lista = cercanas
    .map(c => `"${c.g.titulo}" (${c.horas < 48 ? `${c.horas.toFixed(1)} h` : `${(c.horas / 24).toFixed(0)} días`} de diferencia)`)
    .join('; ');

  return `Ninguna de las ${grabaciones.length} grabaciones encaja. Las más cercanas: ${lista}.`;
}

/**
 * Identificador de la transcripción de una grabación, si tiene.
 *
 * La petición va contra el sitio donde vive el vídeo (`sitio`), no contra el
 * OneDrive de quien mira. Si el vídeo lo grabó otra persona, o está en el
 * SharePoint de un canal, componer la ruta con el correo propio da un 404.
 *
 * @param {string} tokenSharePoint - token para ESE host de SharePoint
 * @param {{sitio: string, driveId: string, itemId: string}} grabacion
 */
export async function idDeTranscripcion(tokenSharePoint, { sitio, driveId, itemId }) {
  if (!sitio) {
    throw new GrabacionesError(
      'No se sabe en qué sitio de SharePoint vive el vídeo, así que no se puede pedir su transcripción.',
      { code: 'SIN_SITIO' }
    );
  }

  const url = `${sitio}/_api/v2.1/drives/${driveId}/items/${itemId}/media/transcripts/`;

  const respuesta = await fetch(url, {
    headers: { Authorization: `Bearer ${tokenSharePoint}`, Accept: 'application/json' }
  });

  if (!respuesta.ok) {
    throw new GrabacionesError(
      `SharePoint respondió ${respuesta.status} al listar las transcripciones`,
      { status: respuesta.status }
    );
  }

  const datos = await respuesta.json();
  const primera = datos.value && datos.value[0];
  return primera ? primera.id : null;
}

/**
 * La transcripción completa, en el JSON que devuelve Teams
 * (`{ transcriptItems: [{ speakerDisplayName, text, startTime, endTime }] }`).
 */
export async function obtenerTranscripcion(tokenSharePoint, grabacion) {
  const { sitio, driveId, itemId } = grabacion;

  // Una transcripción no cambia: la de una reunión de la semana pasada será
  // siempre la misma. Volver a una reunión ya vista debe ser instantáneo.
  const clave = `${driveId}:${itemId}`;
  if (cacheTranscripciones.has(clave)) {
    return cacheTranscripciones.get(clave);
  }

  const id = grabacion.transcriptId ||
    await idDeTranscripcion(tokenSharePoint, { sitio, driveId, itemId });

  if (!id) {
    throw new GrabacionesError(
      'La grabación existe, pero Teams no guardó transcripción de ella. ' +
      'La transcripción hay que activarla al grabar; no se puede generar después.',
      { status: 404 }
    );
  }

  const url = `${sitio}/_api/v2.1/drives/${driveId}/items/${itemId}/media/transcripts/${id}` +
    `/streamContent?format=json&applyhighlights=false&applymediaedits=false`;

  const respuesta = await fetch(url, {
    headers: { Authorization: `Bearer ${tokenSharePoint}`, Accept: 'application/json' }
  });

  if (!respuesta.ok) {
    throw new GrabacionesError(`No se pudo leer la transcripción (${respuesta.status})`, { status: respuesta.status });
  }

  const datos = await respuesta.json();
  cacheTranscripciones.set(clave, datos);
  return datos;
}

/**
 * Una URL de reproducción fresca.
 *
 * La `downloadUrl` del listado caduca en cosa de una hora. Si al abrir el
 * detalle ya no sirve —o el listado no la trajo—, se vuelve a pedir.
 */
export async function urlDeReproduccion(token, { driveId, itemId }) {
  // Sin `$select`: la URL de descarga no es un campo del elemento sino una
  // anotación (`@microsoft.graph.downloadUrl`), y al pedir una selección
  // concreta Graph deja de incluirla. Era justo lo que la dejaba en nulo.
  const item = await graph(`/drives/${driveId}/items/${itemId}`, token);
  return item['@microsoft.graph.downloadUrl'] ||
    (item.remoteItem && item.remoteItem['@microsoft.graph.downloadUrl']) ||
    null;
}

const teamsGrabaciones = {
  listarGrabaciones,
  listarGrabacionesConDetalle,
  grabacionesCompartidas,
  buscarGrabaciones,
  olvidarGrabaciones,
  baseDeSitio,
  selloDelNombre,
  emparejar,
  porQueNoEmpareja,
  idDeTranscripcion,
  obtenerTranscripcion,
  urlDeReproduccion,
  GrabacionesError
};
export default teamsGrabaciones;
